import { useEffect, useState } from 'react';
import { Activity, ArrowLeft, Award, BookOpen, CalendarDays, ChevronRight, ClipboardCheck, FileText, FolderOpen, Images, MessageCircle, ScrollText } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { loadCourseBundle, loadCourseVideoDirectory, loadCourseVideoFolderTrail } from '../lib/data';
import { issueCertificate, loadSingleCourseProgress } from '../lib/certificates';
import type { CourseProgress } from '../lib/certificates';
import type { Course } from '../lib/types';
import { pointer, queryClass, type ParseRecord } from '../lib/parse';
import { listOfflineLectures, subscribeOfflineDownloads } from '../lib/offlineDownloads';
import { formatRupees } from '../lib/subscribe';
import { Badge, Button, CourseArtwork, ErrorState, ImageWithFallback, LoadingState, ProgressBar, SectionHeading } from '../components/ui';

type CourseTab = 'overview' | 'syllabus' | 'materials';
type CourseSubject = ParseRecord & { SubjectName?: string; SubjectDesc?: string; SubjectLogo?: unknown; Topics?: string };
type CourseVideo = ParseRecord & { Type?: string; DisplayOrder?: number | string; VideoName?: string; VideoDuration?: number | string; VideoPaidType?: string; OriginalFileURL?: string; VideoLogo?: unknown; SubjectsPtr?: unknown; ParentFolder?: unknown };
type CourseMaterial = ParseRecord & { Title?: string; Name?: string; Description?: string; FileType?: string; Link?: string; PaidType?: string };
type CourseBundle = { course?: Course; subjects: CourseSubject[]; materials: CourseMaterial[]; tests: ParseRecord[]; lectureCount: number; materialCount: number; testCount: number; unassignedRootCount: number; contentAllowed?: boolean };

function formatLectureDuration(value: unknown) {
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? `${Math.round(seconds / 60)} min` : 'Lecture';
}

function VideoLectureRow({ video, index, returnTo }: { video: CourseVideo; index: number; returnTo: string }) {
  const available = Boolean(video.OriginalFileURL);
  const content = <>
    <CourseArtwork index={index} icon="play" image={video.VideoLogo} />
    <div>
      <strong>{video.VideoName || 'Untitled lecture'}</strong>
      <small>{formatLectureDuration(video.VideoDuration)} · {available ? (video.VideoPaidType || 'Ready to play') : 'Video coming soon'}</small>
    </div>
    <ChevronRight size={17} aria-hidden="true" />
  </>;
  return available
    ? <Link className="subject-row video-row" to={`/video/${video.objectId}`} state={{ returnTo }} onClick={(event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      try { sessionStorage.setItem(SYLLABUS_RETURN_KEY, JSON.stringify({ returnTo, returnScrollY: window.scrollY })); } catch { /* storage can be unavailable in private browsing */ }
    }}>{content}</Link>
    : <div className="subject-row video-row video-row-unavailable" aria-disabled="true">{content}</div>;
}

type SyllabusFolder = { objectId: string; title: string };
type SyllabusDirectory = { key: string; items: CourseVideo[]; trail: SyllabusFolder[] };
const DIRECTORY_PAGE_SIZE = 100;
const SYLLABUS_RETURN_KEY = 'ayurguide-course-syllabus-return';

function safeLoadedCount(value: string | null) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= DIRECTORY_PAGE_SIZE ? Math.floor(parsed) : DIRECTORY_PAGE_SIZE;
}

export function StudentCourseDetail() {
  const { courseId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const subjectId = searchParams.get('subject') || undefined;
  const folderId = searchParams.get('folder') || undefined;
  const directorySubjectId = subjectId === 'unassigned' ? undefined : subjectId;
  const loadedCount = safeLoadedCount(searchParams.get('loaded'));
  const [bundle, setBundle] = useState<CourseBundle>();
  const activeTab: CourseTab = bundle?.contentAllowed === false ? 'overview' : requestedTab === 'syllabus' || requestedTab === 'materials' ? requestedTab : 'overview';
  const [loading, setLoading] = useState(true);
  const [directory, setDirectory] = useState<SyllabusDirectory>();
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState('');
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [downloadCount, setDownloadCount] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (active) { setBundle(undefined); setLoading(true); }
      if (courseId.startsWith('fallback')) { if (active) setLoading(false); return; }
      try {
        const value = await loadCourseBundle(courseId);
        if (active) setBundle(value as CourseBundle);
      } catch {
        if (active) setBundle(undefined);
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [courseId]);

  useEffect(() => {
    let active = true;
    if (activeTab !== 'syllabus' || !subjectId || !bundle || bundle.course?.objectId !== courseId) {
      setDirectory(undefined);
      setDirectoryError('');
      setDirectoryLoading(false);
      return () => { active = false; };
    }
    if (subjectId !== 'unassigned' && !bundle.subjects.some((subject) => subject.objectId === subjectId)) {
      setDirectory(undefined);
      setDirectoryError('This subject is not available in this course.');
      setDirectoryLoading(false);
      return () => { active = false; };
    }
    const key = `${courseId}:${subjectId}:${folderId || 'root'}`;
    setDirectory(undefined);
    setDirectoryError('');
    setDirectoryLoading(true);
    void (async () => {
      try {
        const [items, trail] = await Promise.all([
          loadCourseVideoDirectory(courseId, directorySubjectId, folderId),
          loadCourseVideoFolderTrail(courseId, directorySubjectId, folderId),
        ]);
        if (active) setDirectory({ key, items: items as CourseVideo[], trail });
      } catch {
        if (active) setDirectoryError('Could not load this syllabus folder. Check your connection and try again.');
      } finally {
        if (active) setDirectoryLoading(false);
      }
    })();
    return () => { active = false; };
  }, [activeTab, bundle, courseId, directorySubjectId, folderId, subjectId]);

  useEffect(() => {
    if (!courseId || courseId.startsWith('fallback')) return undefined;
    let active = true;
    void loadSingleCourseProgress(courseId).then((summary) => { if (active) setProgress(summary); }).catch(() => undefined);
    return () => { active = false; };
  }, [courseId]);

  useEffect(() => {
    if (!bundle) return undefined;
    let active = true;
    const refreshCount = () => {
      void (async () => {
        try {
          const savedIds = [...new Set((await listOfflineLectures()).map((lecture) => lecture.id).filter(Boolean))];
          let count = 0;
          for (let offset = 0; offset < savedIds.length; offset += 100) {
            const ids = savedIds.slice(offset, offset + 100);
            const matching = await queryClass('elearning_Videos', {
              limit: ids.length,
              keys: ['objectId'],
              appScoped: false,
              where: (query) => {
                query.containedIn('objectId', ids);
                query.equalTo('CoursePtr', pointer('elearning_Course', courseId));
              },
            });
            count += matching.length;
          }
          if (active) setDownloadCount(count);
        } catch {
          if (active) setDownloadCount(0);
        }
      })();
    };
    refreshCount();
    const unsubscribe = subscribeOfflineDownloads(refreshCount);
    return () => { active = false; unsubscribe(); };
  }, [bundle, courseId]);

  const restoreScrollY = (location.state as { restoreScrollY?: unknown } | null)?.restoreScrollY;
  useEffect(() => {
    if (loading || directoryLoading) return undefined;
    let savedReturn: { returnTo?: string; returnScrollY?: number } | undefined;
    try { savedReturn = JSON.parse(sessionStorage.getItem(SYLLABUS_RETURN_KEY) || 'null') ?? undefined; } catch { savedReturn = undefined; }
    const currentRoute = `${location.pathname}${location.search}`;
    const storedScrollY = savedReturn?.returnTo === currentRoute && typeof savedReturn.returnScrollY === 'number' ? savedReturn.returnScrollY : undefined;
    const targetScrollY = typeof restoreScrollY === 'number' ? restoreScrollY : storedScrollY;
    if (targetScrollY === undefined) return undefined;
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo(0, Math.max(0, targetScrollY));
      try { sessionStorage.removeItem(SYLLABUS_RETURN_KEY); } catch { /* storage can be unavailable in private browsing */ }
      if (typeof restoreScrollY === 'number') navigate(currentRoute, { replace: true, state: null });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [directory?.key, directoryLoading, loading, location.pathname, location.search, navigate, restoreScrollY]);

  const setCourseTab = (tab: CourseTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next);
  };

  const syllabusHref = (nextSubjectId?: string, nextFolderId?: string, nextLoadedCount?: number) => {
    const next = new URLSearchParams({ tab: 'syllabus' });
    if (nextSubjectId) next.set('subject', nextSubjectId);
    if (nextFolderId) next.set('folder', nextFolderId);
    if (nextLoadedCount && nextLoadedCount > DIRECTORY_PAGE_SIZE) next.set('loaded', String(nextLoadedCount));
    return `/courses/${courseId}?${next.toString()}`;
  };

  const loadMoreDirectoryItems = () => {
    const next = new URLSearchParams(searchParams);
    next.set('loaded', String(Math.min(directory?.items.length ?? loadedCount, loadedCount + DIRECTORY_PAGE_SIZE)));
    setSearchParams(next);
  };

  const claimCertificate = async () => {
    if (!progress?.complete || !courseId) return;
    setClaiming(true);
    setClaimError('');
    try {
      const cert = await issueCertificate(courseId);
      if (cert?.objectId) navigate(`/certificates/${cert.objectId}`);
      else setClaimError('The certificate could not be created. Please try again.');
    } catch (error) {
      setClaimError(error instanceof Error ? error.message : 'The certificate could not be created. Please try again.');
    } finally { setClaiming(false); }
  };

  if (loading) return <LoadingState />;
  const course = bundle?.course;
  if (!course && !courseId.startsWith('fallback')) return <ErrorState onRetry={() => window.location.reload()} />;

  const title = course?.CourseTitle ?? 'BAMS Foundation';
  const description = course?.CourseDescription?.trim();
  const materials = bundle?.materials ?? [];
  const subjects = bundle?.subjects ?? [];
  const selectedSubject = subjects.find((subject) => subject.objectId === subjectId);
  const expectedDirectoryKey = subjectId ? `${courseId}:${subjectId}:${folderId || 'root'}` : '';
  const currentDirectory = directory?.key === expectedDirectoryKey ? directory : undefined;
  const visibleItems = currentDirectory?.items.slice(0, loadedCount) ?? [];
  const hasMoreDirectoryItems = Boolean(currentDirectory && visibleItems.length < currentDirectory.items.length);
  const price = Number(course?.Price ?? 0);
  const showPurchase = course?.CoursePaidType?.toLowerCase() === 'paid' && Number.isFinite(price) && price > 0;
  const purchaseUrl = `/subscribe?courseId=${encodeURIComponent(courseId)}`;
  const contentAllowed = bundle?.contentAllowed !== false;
  const lectureCount = bundle?.lectureCount ?? 0;
  const materialCount = bundle?.materialCount ?? materials.length;
  const testCount = bundle?.testCount ?? bundle?.tests.length ?? 0;
  const coursePath = `/courses/${courseId}`;
  const onCourseOverview = location.pathname === coursePath;
  const courseSections = [
    { key: 'overview', label: 'Overview', icon: BookOpen, href: coursePath, active: onCourseOverview && activeTab === 'overview', visible: true },
    { key: 'syllabus', label: 'Syllabus', icon: ScrollText, href: `${coursePath}?tab=syllabus`, active: onCourseOverview && activeTab === 'syllabus', visible: contentAllowed },
    { key: 'materials', label: 'Materials', icon: FileText, href: `${coursePath}?tab=materials`, active: onCourseOverview && activeTab === 'materials', visible: contentAllowed },
    { key: 'tests', label: 'Tests', icon: ClipboardCheck, href: `${coursePath}/tests`, active: false, visible: contentAllowed },
    { key: 'performance', label: 'Performance', icon: Activity, href: `${coursePath}/performance`, active: false, visible: contentAllowed && (course?.Performance === true || testCount > 0) },
    { key: 'stories', label: 'Stories', icon: Images, href: `${coursePath}/stories`, active: false, visible: contentAllowed && course?.Stories === true },
    { key: 'chat', label: 'Chat', icon: MessageCircle, href: `${coursePath}/chat`, active: false, visible: contentAllowed && course?.Chat === true },
    { key: 'qod', label: 'Question of the day', icon: CalendarDays, href: '/qod', active: false, visible: contentAllowed && course?.QOD === true },
  ].filter((section) => section.visible);

  return <div className="detail-page">
    <Link to="/courses" className="back-link"><ArrowLeft size={17} /> My courses</Link>
    <section className="detail-hero">
      <CourseArtwork index={0} image={course?.CourseBannerImg ?? course?.CourseLogo} />
      <div className="detail-hero-copy">
        <Badge>{course?.CoursePaidType ?? 'Course'}</Badge>
        <h1>{title}</h1>
        <p>{course?.CourseSubTitle ?? 'AyurGuide learning batch'}</p>
        {showPurchase && <div className="detail-purchase"><strong>{formatRupees(price)}</strong><Link className="btn btn-soft" to={purchaseUrl}>Purchase options</Link></div>}
      </div>
    </section>
    <nav className="course-section-tabs" aria-label="Course sections">
      {courseSections.map(({ key, label, icon: Icon, href, active }) => <Link className={`course-section-tab${active ? ' active' : ''}`} to={href} aria-current={active ? 'page' : undefined} key={key}>
        <Icon size={16} aria-hidden="true" />
        <span>{label}</span>
      </Link>)}
    </nav>
    {contentAllowed && <section className="detail-summary" aria-label="Course content summary">
      <button type="button" onClick={() => setCourseTab('syllabus')} aria-label={`Browse ${lectureCount} lectures`}><strong>{lectureCount}</strong><span>Lectures</span></button>
      <Link to={`/courses/${courseId}/tests`} aria-label={`Browse ${testCount} available tests`}><strong>{testCount}</strong><span>Tests available</span></Link>
      <button type="button" onClick={() => setCourseTab('materials')} aria-label={`Browse ${materialCount} course materials`}><strong>{materialCount}</strong><span>Materials</span></button>
      <div aria-label={`${downloadCount} course lectures saved for offline use`}><strong>{downloadCount}</strong><span>Downloaded</span></div>
    </section>}
    {activeTab === 'overview' && <section id="course-overview" className="detail-section" aria-label="Overview">
      <SectionHeading title="About this course" />
      {description ? <p className="detail-description">{description}</p> : <p className="detail-description">Course information will be available soon.</p>}
      {!contentAllowed && <p className="detail-description">Purchase this course to access its syllabus, tests, and materials.</p>}
      {progress && <div className="detail-progress-section"><SectionHeading title="Your progress" /><div className="progress-row"><ProgressBar value={progress.percent} completed={progress.complete} /><strong className="cert-percent">{progress.percent}%</strong></div><small className="progress-note">{progress.completedLectures} of {progress.totalLectures} lectures finished{progress.complete ? ' · course complete!' : ''}</small>{claimError && <p className="form-error" role="alert">{claimError}</p>}{progress.complete && <Button icon={Award} disabled={claiming} onClick={() => void claimCertificate()}>{claiming ? 'Claiming…' : 'Get certificate'}</Button>}</div>}
    </section>}
    {activeTab === 'syllabus' && <section id="course-syllabus" className="detail-section" aria-label="Syllabus">
      <SectionHeading title="Syllabus" />
      {!subjectId ? <>
        {subjects.length > 0 || (bundle?.unassignedRootCount ?? 0) > 0
          ? <div className="subject-list syllabus-subject-list">
            {subjects.map((subject, index) => <Link className="subject-row syllabus-entry-row" to={syllabusHref(subject.objectId)} key={subject.objectId}>
              <span><ImageWithFallback src={subject.SubjectLogo} alt="" className="syllabus-subject-image" fallback={<ScrollText size={18} />} /></span>
              <div><strong>{subject.SubjectName || `Subject ${index + 1}`}</strong><small>Open subject</small></div>
              <ChevronRight size={17} aria-hidden="true" />
            </Link>)}
            {(bundle?.unassignedRootCount ?? 0) > 0 && <Link className="subject-row syllabus-entry-row" to={syllabusHref('unassigned')}>
              <span><ScrollText size={18} /></span>
              <div><strong>Other lectures</strong><small>{bundle?.unassignedRootCount} items · Open</small></div>
              <ChevronRight size={17} aria-hidden="true" />
            </Link>}
          </div>
          : <p className="detail-description">No subjects are available in this syllabus yet.</p>}
      </> : directoryLoading && !currentDirectory ? <LoadingState /> : directoryError ? <p className="form-error" role="alert">{directoryError}</p> : currentDirectory && <>
        <nav className="syllabus-breadcrumb" aria-label="Syllabus location">
          <Link to={syllabusHref()}>All subjects</Link>
          <span className="syllabus-crumb"><ChevronRight size={14} aria-hidden="true" />
            {folderId
              ? <Link to={syllabusHref(subjectId)}>{subjectId === 'unassigned' ? 'Other lectures' : selectedSubject?.SubjectName || 'Subject'}</Link>
              : <strong aria-current="page">{subjectId === 'unassigned' ? 'Other lectures' : selectedSubject?.SubjectName || 'Subject'}</strong>}
          </span>
          {currentDirectory.trail.map((folder, index) => {
            const current = index === currentDirectory.trail.length - 1;
            const parentId = index > 0 ? currentDirectory.trail[index - 1].objectId : undefined;
            return <span className="syllabus-crumb" key={folder.objectId}><ChevronRight size={14} aria-hidden="true" />
              {current ? <strong aria-current="page">{folder.title}</strong> : <Link to={syllabusHref(subjectId, parentId)}>{folder.title}</Link>}
            </span>;
          })}
        </nav>
        <header className="syllabus-directory-heading">
          <span className="syllabus-subject-art">{currentDirectory.trail.length ? <FolderOpen size={19} /> : selectedSubject ? <ImageWithFallback src={selectedSubject.SubjectLogo} alt="" className="syllabus-subject-image" fallback={<ScrollText size={18} />} /> : <ScrollText size={18} />}</span>
          <div><h3>{currentDirectory.trail[currentDirectory.trail.length - 1]?.title || (subjectId === 'unassigned' ? 'Other lectures' : selectedSubject?.SubjectName || 'Subject')}</h3><small>{currentDirectory.items.length.toLocaleString('en-IN')} items in this location</small></div>
        </header>
        {!folderId && selectedSubject?.SubjectDesc && <p className="detail-description syllabus-description">{selectedSubject.SubjectDesc}</p>}
        {!folderId && selectedSubject?.Topics && <p className="syllabus-topics">Topics: {selectedSubject.Topics}</p>}
        {visibleItems.length > 0
          ? <div className="subject-list syllabus-directory-list">{visibleItems.map((item, index) => item.Type === 'Folder'
            ? <Link className="subject-row syllabus-entry-row" to={syllabusHref(subjectId, item.objectId)} key={item.objectId}>
              <span><FolderOpen size={18} /></span>
              <div><strong>{item.VideoName || 'Untitled folder'}</strong><small>Open folder</small></div>
              <ChevronRight size={17} aria-hidden="true" />
            </Link>
            : <VideoLectureRow key={item.objectId} video={item} index={index} returnTo={syllabusHref(subjectId, folderId, visibleItems.length)} />)}</div>
          : !directoryLoading && <p className="detail-description syllabus-empty">This location is empty.</p>}
        {hasMoreDirectoryItems && <div className="load-more-lectures"><p>Showing {visibleItems.length.toLocaleString('en-IN')} of {currentDirectory.items.length.toLocaleString('en-IN')} items</p><Button variant="soft" onClick={loadMoreDirectoryItems}>Load more</Button></div>}
      </>}
    </section>}
    {activeTab === 'materials' && <section id="course-materials" className="detail-section" aria-label="Materials">
      <SectionHeading title="Materials" />
      {materials.length ? <div className="subject-list detail-material-list">{materials.map((material) => <Link className="subject-row material-row" to={`/library/item/${material.objectId}`} key={material.objectId}><span><FileText size={17} /></span><div><strong>{material.Title ?? material.Name ?? 'Untitled material'}</strong><small>{material.FileType ?? 'Study material'}{material.PaidType ? ` · ${material.PaidType}` : ''}</small></div><ChevronRight size={17} /></Link>)}</div> : <p className="detail-description">No published materials in this course yet.</p>}
    </section>}
  </div>;
}
